import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useParams, Link } from "react-router-dom";
import projects from "./projects";
import axios from "../api/axiosInstance";

// Reusable component to show a tech stack item with logo + label
const TechBadge = ({ name, logo }) => (
  <motion.div
    className="flex flex-col items-center bg-white/5 backdrop-blur-md p-4 rounded-2xl shadow-md hover:shadow-lg transition"
    whileHover={{ scale: 1.05 }}
  >
    {/* Tech logo image */}
    <img
      src={logo}
      alt={name}
      className="w-12 h-12 mb-2 object-contain"
      onError={(e) => (e.currentTarget.style.display = "none")}
    />
    {/* Fallback: Tech name */}
    <span className="text-sm text-white/90">{name}</span>
  </motion.div>
);

const ProjectDetail = () => {
  const { id } = useParams(); // get project ID from URL
  const project = projects.find((p) => p.id === id);

  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loadingBlogs, setLoadingBlogs] = useState(true);

  useEffect(() => {
    if (!project) return;
    setLoadingBlogs(true);
    axios.get(`/blog?projectSlug=${id}`)
      .then((res) => {
        setRelatedBlogs(res.data || []);
      })
      .catch((err) => {
        console.error("Failed to fetch related blogs:", err);
      })
      .finally(() => {
        setLoadingBlogs(false);
      });
  }, [id, project]);

  if (!project) return <p className="text-center text-white">Project not found</p>;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-black text-white py-16 px-6">
      <motion.div
        className="max-w-6xl mx-auto"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Project Title + Tagline */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
            {project.title}
          </h1>
          <p className="text-lg text-gray-300">{project.tagline}</p>
        </motion.div>
 
        {/* Cover image or video */}
        {project.cover && (
          <motion.img
            src={project.cover}
            alt="cover"
            className="w-full rounded-3xl shadow-lg mb-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          />
        )}

        {/* Overview Section */}
        <motion.section
          className="mb-12 bg-white/5 backdrop-blur-lg p-8 rounded-2xl shadow-lg"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h2 className="text-2xl font-semibold mb-4">Overview</h2>
          <p className="text-gray-300 leading-relaxed">{project.overview}</p>
        </motion.section>

        {/* Problem & Solution Side by Side */}
        <motion.section
          className="grid md:grid-cols-2 gap-8 mb-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <div className="bg-white/5 p-6 rounded-2xl shadow-md">
            <h3 className="text-xl font-semibold mb-2">Problem</h3>
            <p className="text-gray-300">{project.problem}</p>
          </div>
          <div className="bg-white/5 p-6 rounded-2xl shadow-md">
            <h3 className="text-xl font-semibold mb-2">Solution</h3>
            <p className="text-gray-300">{project.solution}</p>
          </div>
        </motion.section>

        {/* Tech Stack Section */}
        <motion.section
          className="mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <h2 className="text-2xl font-semibold mb-6 text-center">Tech Stack</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-6 justify-items-center">
            {Object.values(project.techStack).flat().map((tech) => (
              <TechBadge
                key={tech}
                name={tech}
                logo={`/assets/tech/${tech.toLowerCase().replace(/ /g, "-")}.svg`}
              />
            ))}
          </div>
        </motion.section>

        {/* Features Section */}
        <motion.section
          className="mb-12 bg-white/5 p-8 rounded-2xl shadow-md"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
        >
          <h2 className="text-2xl font-semibold mb-4">Features</h2>
          <ul className="list-disc pl-5 text-gray-300 space-y-2">
            {project.features.map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>
        </motion.section>

        {/* Media Gallery */}
        {project.media?.length > 0 && (
          <motion.section
            className="mb-12"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <h2 className="text-2xl font-semibold mb-6">Gallery</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {project.media.map((m, i) => (
                <div key={i} className="rounded-xl overflow-hidden shadow-md">
                  {m.type === "image" && <img src={m.src} alt={m.alt} className="w-full" />}
                  {m.type === "video" && (
                    <video controls poster={m.poster} className="w-full">
                      <source src={m.src} type="video/mp4" />
                    </video>
                  )}
                  {m.type === "embed" && (
                    <iframe
                      src={m.src}
                      title={m.title}
                      className="w-full h-64"
                      allowFullScreen
                    />
                  )}
                </div>
              ))}
            </div>
          </motion.section>
        )}

        {/* Related Writeups / Case Studies Section */}
        {!loadingBlogs && relatedBlogs.length > 0 && (
          <motion.section
            className="mb-12"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85 }}
          >
            <h2 className="text-2xl font-semibold mb-6">Related Explanations & Journeys</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {relatedBlogs.map((b) => (
                <Link
                  key={b._id}
                  to={`/blog/${b._id}`}
                  className="bg-white/5 backdrop-blur-lg p-6 rounded-2xl border border-white/5 hover:bg-white/10 hover:border-cyan-500/30 transition block group text-left"
                >
                  <span className="text-xs text-cyan-400 font-bold uppercase tracking-wider block mb-1">
                    {b.category || "Project Deep Dive"}
                  </span>
                  <h4 className="text-lg font-bold mb-2 group-hover:text-cyan-300 transition">
                    {b.title}
                  </h4>
                  <p className="text-sm text-gray-400 line-clamp-2 leading-relaxed">
                    {(b.content || "").replace(/[#*`]/g, '').slice(0, 150)}...
                  </p>
                </Link>
              ))}
            </div>
          </motion.section>
        )}

        {/* Links Section */}
        <motion.section
          className="flex gap-4 justify-center mt-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
        >
          {project.links?.live && (
            <a
              href={project.links.live}
              target="_blank"
              rel="noreferrer"
              className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl shadow-md hover:shadow-lg transition"
            >
              Live Demo
            </a>
          )}
          {project.links?.repo && (
            <a
              href={project.links.repo}
              target="_blank"
              rel="noreferrer"
              className="px-6 py-3 bg-white/10 rounded-xl shadow-md hover:shadow-lg transition"
            >
              GitHub Repo
            </a>
          )}
        </motion.section>
      </motion.div>
    </div>
  );
};

export default ProjectDetail;
